import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import {
  clientManagementUrl,
  setupManagementUrl,
} from "../../../config";
import { allGenders } from "../../../data/Axios/queries";

const base_url = clientManagementUrl.uri;

const IndividualClientsForm = (props) => {
  const [genderData, setGenderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdentificationType, setSelectedIdentificationType] = useState(
    props.data ? props.data.identificationType : ""
  );

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const initialValues = {
    id: props.data ? props.data.id : null,
    firstName: props.data ? props.data.firstName : "",
    lastName: props.data ? props.data.lastName : "",
    dateOfBirth:   props.data && props.data.dateOfBirth
      ? props.data.dateOfBirth.split("T")[0]
      : "",
    genderId: props.data ? props.data.genderId : "",
    identificationType: props.data ? props.data.identificationType : "",
    identificationNumber: props.data ? props.data.identificationNumber : "",
    idDocumentFile: props.data ? props.data.idDocumentFile : null,
    contactNumber: props.data ? props.data.contactNumber : "",
    email: props.data ? props.data.email : "",
    address: props.data ? props.data.address : "",
    occupation: props.data ? props.data.occupation : "",
    employer: props.data ? props.data.employer : "",
    notes: props.data ? props.data.notes : "",
    nationality: props.data ? props.data.nationality : "",
    emergencyContactName: props.data ? props.data.emergencyContactName : "",
    emergencyContactNumber: props.data ? props.data.emergencyContactNumber : "",
    relationshipWithEmergencyContact: props.data ? props.data.relationshipWithEmergencyContact : "",
  };

  useEffect(() => {
    async function fetchAllData() {
      try {
        const genderResponse = await graphqlQuery(
          allGenders,
          setupManagementUrl.uri
        );
        if (genderResponse) {
          setGenderData(genderResponse);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  // ✅ Validation Schema with Kenyan Rules
  const validationSchema = yup.object().shape({
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),

    dateOfBirth: yup
      .date()
      .typeError("Enter a valid date")
      .required("Date of Birth is required")
      .test(
        "is-adult",
        "Client must be at least 18 years old",
        function (value) {
          if (!value) return true;

          const today = new Date();
          const birthDate = new Date(value);

          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          return age >= 18;
        }
      ),

    contactNumber: yup
      .string()
      .matches(/^(0[17]\d{8}|\+254\d{9})$/, "Enter a valid contact number")
      .required("Contact Number is required"),

    email: yup
      .string()
      .email("Enter a valid email address")
      .required("Email is required"),

    genderId: yup.string().required("Gender is required"),

    identificationType: yup
      .string()
      .required("Identification Type is required"),

    identificationNumber: yup
      .string()
      .when("identificationType", {
        is: (val) => val === "national_id" || val === "passport",
        then: yup
          .string()
          .matches(/^\d{8,11}$/, "National ID must be numeric and at least 8 to 11 digits")
          .required("Identification Number is required"),
      }),

    address: yup.string().required("Address is required"),
    occupation: yup.string().nullable(),
    employer: yup.string().nullable(),
    notes: yup.string().nullable(),
    nationality: yup.string().nullable(),
    emergencyContactName: yup.string().nullable(),
    emergencyContactNumber: yup.string().nullable(),
    relationshipWithEmergencyContact: yup.string().nullable(),
    idDocumentFile: yup.mixed().nullable(),
  });

  const genderOptions = genderData
    ? genderData.map((gender) => ({
        value: gender.id,
        label: gender.gender,
      }))
    : [];

  const identificationOptions = [
    { value: "national_id", label: "National ID" },
    { value: "passport", label: "Passport" },
  ];

  const handleIdentificationTypeChange = (event, formikHelpers) => {
    const newValue = event.target.value;
    setSelectedIdentificationType(newValue);
    formikHelpers.setFieldValue("identificationType", newValue);
    formikHelpers.setFieldValue("identificationNumber", ""); // Reset number on type change
    formikHelpers.setFieldValue("idDocumentFile", null); // Reset file on type change
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        firstName,
        lastName,
        dateOfBirth,
        email,
        contactNumber,
        identificationType,
        identificationNumber,
        idDocumentFile,
        genderId,
        address,
        occupation,
        employer,
        notes,
        nationality,
        emergencyContactName,
        emergencyContactNumber,
        relationshipWithEmergencyContact,
      } = values;

      const parsedGenderId = parseInt(genderId) || null;
      const formattedDateOfBirth = dateOfBirth ? new Date(dateOfBirth).toISOString().split('T')[0] : null;

      const mutationData = props.isEditing
        ? {
            id: props.data.id,
            firstName,
            lastName,
            dateOfBirth: formattedDateOfBirth,
            email,
            contactNumber,
            identificationType,
            identificationNumber,
            genderId: parsedGenderId,
            address,
            occupation,
            employer,
            notes,
            nationality,
            emergencyContactName,
            emergencyContactNumber,
            relationshipWithEmergencyContact,
          }
        : {
            firstName,
            lastName,
            dateOfBirth: formattedDateOfBirth,
            email,
            contactNumber,
            identificationType,
            identificationNumber,
            genderId: parsedGenderId,
            address,
            occupation,
            employer,
            notes,
            nationality,
            emergencyContactName,
            emergencyContactNumber,
            relationshipWithEmergencyContact,
            createdBy: userId,
            companyId,
            isActive: values.isActive ? 1 : 0,
          };

      // TODO: Handle file upload separately (e.g., via multipart form or presigned URL)
      if (idDocumentFile) {
        console.log("ID Document File selected for upload:", idDocumentFile.name);
        // Example: Implement file upload logic here and add file reference to mutationData if needed
      }

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateIndividualClient" : "createIndividualClient",
        mutationData,
        props.isEditing ? "updatedIndividualClient" : "newIndividualClient",
        base_url
      );

      if (response) {
        props.onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Client ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        }).then(() => {
          props.refetchData(); // Refresh table after success
        });
      }
    } catch (error) {
      console.error("Mutation error:", error);
       const errorMessage = error.message || "An unexpected error occurred.";
  Swal.fire({
    icon: "error",
    title: "Error!",
    text: errorMessage,
  });
    }

    setSubmitting(false);
  };

  const isNonMobile = useMediaQuery("(min-width:600px)");

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb={1}>
        {props.isEditing ? "Edit Individual Client" : "Create Individual Client"}
      </Typography>
      <Typography variant="subtitle1" mb={3} color="text.secondary">
        {props.isEditing
          ? "Edit an Existing Individual Client"
          : "Create a New Individual Client"}
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {
          const uploadLabel = values.identificationType === "national_id" 
            ? "Upload your ID Document (Optional)" 
            : values.identificationType === "passport" 
            ? "Upload your Passport Document (Optional)" 
            : "Attach KYC Document (Optional)";

          return (
            <Form onSubmit={handleSubmit}>
              <Box display="grid" gap="20px" gridTemplateColumns={isNonMobile ? "repeat(2, 1fr)" : "1fr"}>
                {/* Row 1: First Name & Last Name */}
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="firstName"
                    label="First Name *"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName ? errors.firstName : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="lastName"
                    label="Last Name *"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName ? errors.lastName : ""}
                  />
                </Box>

                {/* Row 2: Date of Birth & Contact Number */}
                <Box>
                  <TextField
                    fullWidth
                    type="date"
                    name="dateOfBirth"
                    label="Date of Birth "
                    value={values.dateOfBirth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(touched.dateOfBirth && errors.dateOfBirth)}
                    helperText={touched.dateOfBirth && errors.dateOfBirth ? errors.dateOfBirth : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="contactNumber"
                    label="Contact Number *"
                    value={values.contactNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.contactNumber && errors.contactNumber)}
                    helperText={touched.contactNumber && errors.contactNumber ? errors.contactNumber : ""}
                  />
                </Box>

                {/* Row 3: Email & Gender */}
                <Box>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    label="Email *"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email ? errors.email : ""}
                  />
                </Box>
                <Box>
                  <FormControl fullWidth error={Boolean(touched.genderId && errors.genderId)}>
                    <InputLabel>Gender *</InputLabel>
                    <Select
                      name="genderId"
                      value={values.genderId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Gender *"
                    >
                      <MenuItem value="" disabled>
                        Select Gender
                      </MenuItem>
                      {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched.genderId && errors.genderId ? errors.genderId : ""}
                    </FormHelperText>
                  </FormControl>
                </Box>

                {/* Row 4: Identification Type & Identification Number */}
                <Box>
                  <FormControl fullWidth error={Boolean(touched.identificationType && errors.identificationType)}>
                    <InputLabel>Identification Type *</InputLabel>
                    <Select
                      name="identificationType"
                      value={values.identificationType}
                      onChange={(e) => handleIdentificationTypeChange(e, { setFieldValue })}
                      onBlur={handleBlur}
                      label="Identification Type *"
                    >
                      <MenuItem value="" disabled>
                        Select Type
                      </MenuItem>
                      {identificationOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {touched.identificationType && errors.identificationType ? errors.identificationType : ""}
                    </FormHelperText>
                  </FormControl>
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="identificationNumber"
                    label={
                      values.identificationType === "passport"
                        ? "Passport Number *"
                        : values.identificationType === "national_id"
                        ? "National ID *"
                        : "Identification Number *"
                    }
                    value={values.identificationNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.identificationNumber && errors.identificationNumber)}
                    helperText={
                      touched.identificationNumber && errors.identificationNumber
                        ? errors.identificationNumber
                        : ""
                    }
                  />
                </Box>

                {/* Conditional ID Document Upload */}
                {values.identificationType && (
                  <Box gridColumn="span 2">
                    <Typography variant="subtitle2" mb={1} color="text.secondary">
                      {uploadLabel}
                    </Typography>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(event) => setFieldValue("idDocumentFile", event.target.files[0])}
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        backgroundColor: "white",
                      }}
                    />
                    {values.idDocumentFile && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Selected: {values.idDocumentFile.name}
                      </Typography>
                    )}
                    {touched.idDocumentFile && errors.idDocumentFile && (
                      <FormHelperText error>{errors.idDocumentFile}</FormHelperText>
                    )}
                  </Box>
                )}

                {/* Row 5: Address & Occupation */}
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="address"
                    label="Address *"
                    value={values.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address ? errors.address : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="occupation"
                    label="Occupation"
                    value={values.occupation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.occupation && errors.occupation)}
                    helperText={touched.occupation && errors.occupation ? errors.occupation : ""}
                  />
                </Box>

                {/* Row 6: Employer & Nationality */}
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="employer"
                    label="Employer"
                    value={values.employer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.employer && errors.employer)}
                    helperText={touched.employer && errors.employer ? errors.employer : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="nationality"
                    label="Nationality"
                    value={values.nationality}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.nationality && errors.nationality)}
                    helperText={touched.nationality && errors.nationality ? errors.nationality : ""}
                  />
                </Box>

                {/* Row 7: Emergency Contact Name & Notes */}
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="emergencyContactName"
                    label="Emergency Contact Name"
                    value={values.emergencyContactName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.emergencyContactName && errors.emergencyContactName)}
                    helperText={touched.emergencyContactName && errors.emergencyContactName ? errors.emergencyContactName : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="notes"
                    label="Notes"
                    value={values.notes}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.notes && errors.notes)}
                    helperText={touched.notes && errors.notes ? errors.notes : ""}
                  />
                </Box>

                {/* Row 8: Emergency Contact Number & Relationship */}
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="emergencyContactNumber"
                    label="Emergency Contact Number"
                    value={values.emergencyContactNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.emergencyContactNumber && errors.emergencyContactNumber)}
                    helperText={touched.emergencyContactNumber && errors.emergencyContactNumber ? errors.emergencyContactNumber : ""}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    type="text"
                    name="relationshipWithEmergencyContact"
                    label="Relationship with Emergency Contact"
                    value={values.relationshipWithEmergencyContact}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.relationshipWithEmergencyContact && errors.relationshipWithEmergencyContact)}
                    helperText={touched.relationshipWithEmergencyContact && errors.relationshipWithEmergencyContact ? errors.relationshipWithEmergencyContact : ""}
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  sx={{
                    backgroundColor: "#00a62cff",
                    color: "#FFFFFF",
                    '&:hover': {
                      backgroundColor: '#00A676',
                      boxShadow: '0px 4px 12px rgba(0, 166, 118, 0.3)',
                    },
                    '&:disabled': {
                      backgroundColor: '#B0B0B0',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  {isSubmitting
                    ? props.isEditing
                      ? "Updating..."
                      : "Saving..."
                    : props.isEditing
                    ? "Update Client"
                    : "Save Client"}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default IndividualClientsForm;